import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
} from '@aaas/shared';
import {
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
} from '../hooks/useCalendarQueries';
import {
  useEmailTemplateListQuery,
  usePreviewEmailMutation,
} from '../hooks/useEmailQueries';
import { useMemberRoleListQuery } from '../hooks/useMemberRoleQueries';
import { useCalendarEventTypeListQuery } from '../hooks/useCalendarEventTypeQueries';
import calendarEventTypeService from '../services/calendar-event-type.service';
import {
  CalendarAttendeeSection,
  type WorkspaceMember,
} from './CalendarAttendeeSection';
import { TiptapEmailEditor } from './TiptapEmailEditor';
import type {
  CalendarEvent,
  CalendarEventCreateInput,
  CalendarEventUpdateInput,
  CalendarAttendeeCreateInput,
  CalendarAttendeeRole,
} from '../types';

function applyVariables(source: string, vars: Record<string, string>): string {
  let result = source;
  for (const [key, value] of Object.entries(vars)) {
    result = result.split(`{{${key}}}`).join(value);
  }
  return result;
}

interface CalendarEventFormDialogProps {
  event: CalendarEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cvId?: string;
  cvName?: string;
  jobId?: string;
}

function toLocalDatetimeString(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function CalendarEventFormDialog({
  event,
  open,
  onOpenChange,
  cvId,
  cvName,
  jobId,
}: CalendarEventFormDialogProps) {
  const { t } = useTranslation('ats');
  const isEdit = !!event;
  const { user, currentWorkspace, idToken } = useAuth();
  const createMutation = useCreateCalendarEventMutation();
  const updateMutation = useUpdateCalendarEventMutation();

  const { data: memberData } = useMemberRoleListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken ?? undefined,
    enabled: open,
  });
  const { data: eventTypesData, refetch: refetchEventTypes } = useCalendarEventTypeListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken ?? undefined,
    size: 100,
    enabled: open,
  });
  const eventTypes = eventTypesData?.data?.items ?? [];

  // Template picker is only relevant at create time when a candidate is linked.
  const templatePickerEnabled = !isEdit && !!cvId;

  const { data: emailTemplatesData, isLoading: emailTemplatesLoading } =
    useEmailTemplateListQuery({
      workspaceId: currentWorkspace?.id,
      idToken: idToken ?? undefined,
      size: 100,
      enabled: open && templatePickerEnabled,
    });
  const emailTemplates = emailTemplatesData?.data?.items ?? [];
  const hasTemplates = emailTemplates.length > 0;
  const members: WorkspaceMember[] = (memberData?.roles ?? []).map((r) => ({
    account_id: r.account_id,
    account_name: r.account_name,
    account_email: r.account_email,
  }));

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [eventTypeId, setEventTypeId] = useState('');
  const [locationType, setLocationType] = useState('online');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState<CalendarAttendeeCreateInput[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBodyHtml, setEditedBodyHtml] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreateEventType, setShowCreateEventType] = useState(false);
  const [newEventTypeName, setNewEventTypeName] = useState('');
  const [isCreatingEventType, setIsCreatingEventType] = useState(false);
  const [createEventTypeError, setCreateEventTypeError] = useState<string | null>(null);

  const handleCreateEventType = async () => {
    if (!user || !currentWorkspace || !newEventTypeName.trim()) return;
    setIsCreatingEventType(true);
    setCreateEventTypeError(null);
    try {
      const token = await user.getIdToken();
      const created = await calendarEventTypeService.createCalendarEventType(
        currentWorkspace.id,
        token,
        { name: newEventTypeName.trim() }
      );
      await refetchEventTypes();
      setEventTypeId(created.id);
      setShowCreateEventType(false);
      setNewEventTypeName('');
    } catch (err: unknown) {
      setCreateEventTypeError(err instanceof Error ? err.message : 'Failed to create event type');
    } finally {
      setIsCreatingEventType(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && event) {
        setTitle(event.title);
        setDescription(event.description || '');
        setStartTime(toLocalDatetimeString(event.start_time));
        setEndTime(toLocalDatetimeString(event.end_time));
        setEventTypeId(event.event_type_id || '');
        setLocationType(event.location_type);
        setLocation(event.location || '');
        setAttendees(
          event.attendees.map((a) => ({
            account_id: a.account_id ?? undefined,
            email: a.email ?? undefined,
            display_name: a.display_name,
            role: a.role ?? 'attendee',
          }))
        );
      } else {
        setTitle('');
        setDescription('');
        setStartTime('');
        setEndTime('');
        setEventTypeId('');
        setLocationType('online');
        setLocation('');
        setAttendees([]);
        setTemplateId('');
        setEditedSubject('');
        setEditedBodyHtml('');
        setPreviewError(null);
      }
      setError(null);
    }
  }, [open, event, isEdit]);

  const handleAddAttendee = (name: string, email: string, role: CalendarAttendeeRole) => {
    setAttendees((prev) => [
      ...prev,
      { email, display_name: name, role },
    ]);
  };

  const handleAddAttendeeFromAccount = (member: WorkspaceMember, role: CalendarAttendeeRole) => {
    // Prevent duplicate
    if (attendees.some((a) => a.account_id === member.account_id)) return;
    setAttendees((prev) => [
      ...prev,
      {
        account_id: member.account_id,
        email: member.account_email,
        display_name: member.account_name,
        role,
      },
    ]);
  };

  const handleRemoveAttendeeByRole = (role: CalendarAttendeeRole, indexInGroup: number) => {
    const roleAttendees = attendees.filter((a) => (a.role ?? 'attendee') === role);
    const target = roleAttendees[indexInGroup];
    if (!target) return;
    const globalIndex = attendees.indexOf(target);
    if (globalIndex !== -1) {
      setAttendees((prev) => prev.filter((_, i) => i !== globalIndex));
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const interviewers = attendees.filter((a) => a.role === 'interviewer');
  const hrPics = attendees.filter((a) => a.role === 'hr');
  const otherAttendees = attendees.filter((a) => !a.role || a.role === 'attendee');

  const requiredFieldsFilled =
    !!title.trim() && !!startTime && !!endTime && endTime > startTime;

  const selectedTemplate = useMemo(
    () => emailTemplates.find((t) => t.id === templateId) ?? null,
    [emailTemplates, templateId]
  );

  const selectedEventTypeName = useMemo(
    () => eventTypes.find((et) => et.id === eventTypeId)?.name ?? '',
    [eventTypes, eventTypeId]
  );

  const previewMutation = usePreviewEmailMutation();
  const previewLoading = previewMutation.isPending;

  const formEventVariables = useMemo<Record<string, string>>(() => {
    const parts = (v: string) => {
      if (!v) return null;
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return { date: `${dd}/${mm}/${yyyy}`, time: `${hh}:${mi}`, full: `${dd}/${mm}/${yyyy} ${hh}:${mi}` };
    };
    const start = parts(startTime);
    const end = parts(endTime);
    return {
      event_title: title.trim(),
      event_type: selectedEventTypeName,
      event_description: description.trim(),
      event_location: location.trim(),
      event_end_time: end?.full ?? '',
      interview_date: start?.date ?? '',
      interview_time: start && end ? `${start.time} - ${end.time}` : '',
      interview_link: locationType === 'online' ? location.trim() : '',
      interview_address: locationType === 'offline' ? location.trim() : '',
    };
  }, [title, description, location, locationType, startTime, endTime, selectedEventTypeName]);

  // When template changes (to a real one), fetch a preview from the backend
  // to render CV/job variables, then overlay event_* vars from the form state.
  useEffect(() => {
    if (!open || isEdit) return;
    if (!templateId || !cvId || !requiredFieldsFilled) {
      return;
    }
    setPreviewError(null);
    previewMutation.mutate(
      {
        template_id: templateId,
        cv_id: cvId,
        job_id: jobId,
      },
      {
        onSuccess: (res) => {
          const base = res.data;
          setEditedSubject(applyVariables(base.subject, formEventVariables));
          setEditedBodyHtml(applyVariables(base.body_html, formEventVariables));
        },
        onError: (err: unknown) => {
          // Fallback: render from raw template if preview fails.
          setPreviewError(err instanceof Error ? err.message : t('calendar.eventForm.previewFailedFallback'));
          if (selectedTemplate) {
            setEditedSubject(applyVariables(selectedTemplate.subject, formEventVariables));
            setEditedBodyHtml(applyVariables(selectedTemplate.body_html, formEventVariables));
          }
        },
      }
    );
    // We intentionally exclude formEventVariables/previewMutation/selectedTemplate
    // to avoid re-fetching on every keystroke; user can re-pick the template to refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, cvId, jobId, open, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(t('calendar.eventForm.errors.titleRequired'));
      return;
    }
    if (!startTime || !endTime) {
      setError(t('calendar.eventForm.errors.timesRequired'));
      return;
    }
    if (endTime <= startTime) {
      setError(t('calendar.eventForm.errors.endAfterStart'));
      return;
    }

    setError(null);

    try {
      if (isEdit && event) {
        const data: CalendarEventUpdateInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          start_time: new Date(startTime).toISOString(),
          end_time: new Date(endTime).toISOString(),
          event_type_id: eventTypeId || undefined,
          location_type: locationType,
          location: location.trim() || undefined,
          attendees,
        };
        await updateMutation.mutateAsync({ eventId: event.id, data });
      } else {
        const useTemplate = templatePickerEnabled && !!templateId;
        const data: CalendarEventCreateInput = {
          title: title.trim(),
          description: description.trim() || undefined,
          start_time: new Date(startTime).toISOString(),
          end_time: new Date(endTime).toISOString(),
          event_type_id: eventTypeId || undefined,
          location_type: locationType as CalendarEventCreateInput['location_type'],
          location: location.trim() || undefined,
          cv_id: cvId,
          job_id: jobId,
          attendees,
          template_id: useTemplate ? templateId : undefined,
          template_subject_override:
            useTemplate && editedSubject ? editedSubject : undefined,
          template_body_html_override:
            useTemplate && editedBodyHtml ? editedBodyHtml : undefined,
        };
        await createMutation.mutateAsync(data);
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('calendar.eventForm.editTitle') : cvName ? t('calendar.eventForm.createForCandidate', { name: cvName }) : t('calendar.eventForm.createTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">{t('calendar.eventForm.titleLabel')}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('calendar.eventForm.titlePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">{t('calendar.eventForm.startTimeLabel')}</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">{t('calendar.eventForm.endTimeLabel')}</Label>
              <Input
                id="endTime"
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('calendar.eventForm.eventTypeLabel')}</Label>
              <Select value={eventTypeId} onValueChange={setEventTypeId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('calendar.eventForm.eventTypePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <div className="max-h-60 overflow-y-auto">
                    {eventTypes.map((et) => (
                      <SelectItem key={et.id} value={et.id}>
                        {et.name}
                      </SelectItem>
                    ))}
                  </div>
                  <div className="border-t mt-1 pt-1">
                    <button
                      type="button"
                      className="flex items-center gap-1 w-full px-2 py-1.5 text-sm text-blue-600 font-medium hover:bg-accent rounded-sm"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setNewEventTypeName('');
                        setCreateEventTypeError(null);
                        setShowCreateEventType(true);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" /> {t('calendar.eventForm.addEventType')}
                    </button>
                  </div>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('calendar.eventForm.locationTypeLabel')}</Label>
              <Select value={locationType} onValueChange={setLocationType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">{t('calendar.eventForm.online')}</SelectItem>
                  <SelectItem value="offline">{t('calendar.eventForm.offline')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">
              {locationType === 'online' ? t('calendar.eventForm.meetingUrlLabel') : t('calendar.eventForm.addressLabel')}
            </Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={
                locationType === 'online'
                  ? t('calendar.eventForm.meetingUrlPlaceholder')
                  : t('calendar.eventForm.addressPlaceholder')
              }
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('calendar.eventForm.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('calendar.eventForm.descriptionPlaceholder')}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          {/* PIC Sections */}
          <div className="space-y-4">
            <Label>{t('calendar.eventForm.picSection')}</Label>

            <CalendarAttendeeSection
              label={t('calendar.eventForm.interviewers')}
              role="interviewer"
              attendees={interviewers}
              onAdd={handleAddAttendee}
              onAddFromAccount={handleAddAttendeeFromAccount}
              onRemove={(idx) => handleRemoveAttendeeByRole('interviewer', idx)}
              disabled={isSubmitting}
              members={members}
            />

            <CalendarAttendeeSection
              label={t('calendar.eventForm.hrPic')}
              role="hr"
              attendees={hrPics}
              onAdd={handleAddAttendee}
              onAddFromAccount={handleAddAttendeeFromAccount}
              onRemove={(idx) => handleRemoveAttendeeByRole('hr', idx)}
              disabled={isSubmitting}
              members={members}
            />

            <CalendarAttendeeSection
              label={t('calendar.eventForm.otherAttendees')}
              role="attendee"
              attendees={otherAttendees}
              onAdd={handleAddAttendee}
              onAddFromAccount={handleAddAttendeeFromAccount}
              onRemove={(idx) => handleRemoveAttendeeByRole('attendee', idx)}
              disabled={isSubmitting}
              members={members}
            />
          </div>

          {!isEdit && (
            <div className="space-y-3 rounded-md border border-gray-200 p-4">
              <div className="space-y-2">
                <Label>{t('calendar.eventForm.templateSection')}</Label>
                {!cvId ? (
                  <>
                    <Select value="__none__">
                      <SelectTrigger disabled>
                        <SelectValue placeholder={t('calendar.eventForm.templateNeedCv')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('calendar.eventForm.templateUnavailable')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('calendar.eventForm.templateOpenFromCv')}
                    </p>
                  </>
                ) : !requiredFieldsFilled ? (
                  <>
                    <Select value="__none__">
                      <SelectTrigger disabled>
                        <SelectValue placeholder={t('calendar.eventForm.templateNeedFields')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('calendar.eventForm.templateUnavailable')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('calendar.eventForm.templateFillFirst')}
                    </p>
                  </>
                ) : (
                  <>
                    <Select
                      value={templateId || '__none__'}
                      onValueChange={(v) => {
                        const next = v === '__none__' ? '' : v;
                        setTemplateId(next);
                        if (!next) {
                          setEditedSubject('');
                          setEditedBodyHtml('');
                          setPreviewError(null);
                        }
                      }}
                    >
                      <SelectTrigger disabled={emailTemplatesLoading}>
                        <SelectValue
                          placeholder={
                            emailTemplatesLoading
                              ? t('calendar.eventForm.templateLoading')
                              : t('calendar.eventForm.templateNone')
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="max-h-60 overflow-y-auto">
                          <SelectItem value="__none__">{t('calendar.eventForm.templateNone')}</SelectItem>
                          {emailTemplates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                              {tpl.category ? ` — ${tpl.category}` : ''}
                            </SelectItem>
                          ))}
                        </div>
                        {!hasTemplates && !emailTemplatesLoading && (
                          <div className="border-t mt-1 pt-1">
                            <a
                              href="/ats/email-templates"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 w-full px-2 py-1.5 text-sm text-blue-600 font-medium hover:bg-accent rounded-sm"
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <Plus className="h-3.5 w-3.5" /> {t('calendar.eventForm.templateCreate')}
                            </a>
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {hasTemplates ? t('calendar.eventForm.templateHelpHas') : t('calendar.eventForm.templateHelpNone')}
                    </p>
                  </>
                )}
              </div>

              {templateId && selectedTemplate && (
                <div className="space-y-2 border-t pt-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{t('calendar.eventForm.previewTitle')}</Label>
                    {previewLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  </div>
                  {previewError && (
                    <p className="text-xs text-amber-600">
                      {t('calendar.eventForm.previewError', { error: previewError })}
                    </p>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="email-subject" className="text-xs text-muted-foreground">{t('calendar.eventForm.subjectLabel')}</Label>
                    <Input
                      id="email-subject"
                      value={editedSubject}
                      onChange={(e) => setEditedSubject(e.target.value)}
                      disabled={isSubmitting || previewLoading}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{t('calendar.eventForm.bodyLabel')}</Label>
                    <TiptapEmailEditor
                      value={editedBodyHtml}
                      onChange={setEditedBodyHtml}
                      disabled={isSubmitting || previewLoading}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('calendar.eventForm.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEdit ? t('calendar.eventForm.saving') : t('calendar.eventForm.creating')}
                </>
              ) : isEdit ? (
                t('calendar.eventForm.saveChanges')
              ) : (
                t('calendar.eventForm.createButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

      {/* Inline Create Event Type Dialog */}
      <Dialog
        open={showCreateEventType}
        onOpenChange={(open) => {
          if (!isCreatingEventType) setShowCreateEventType(open);
        }}
      >
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>{t('calendar.eventForm.createTypeTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newEventTypeName">{t('calendar.eventForm.createTypeNameLabel')}</Label>
              <Input
                id="newEventTypeName"
                value={newEventTypeName}
                onChange={(e) => setNewEventTypeName(e.target.value)}
                placeholder={t('calendar.eventForm.createTypeNamePlaceholder')}
                disabled={isCreatingEventType}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateEventType();
                  }
                }}
              />
            </div>
            {createEventTypeError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{createEventTypeError}</p>
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateEventType(false)}
                disabled={isCreatingEventType}
              >
                {t('calendar.eventForm.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleCreateEventType}
                disabled={isCreatingEventType || !newEventTypeName.trim()}
              >
                {isCreatingEventType ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('calendar.eventForm.createTypeCreating')}
                  </>
                ) : (
                  t('calendar.eventForm.createTypeButton')
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
